import { useCallback, useRef, useState } from "react";
import axios from "axios";
import {
  ActionIcon,
  Button,
  Group,
  rem,
  Select,
  SelectProps
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { Editor } from "@tiptap/react";
import { IconX } from "@tabler/icons-react";
import useTempId from "../hooks/use-temp-id";
import ContentEditor from "./ContentEditor";
import FormAttachments from "./attachments/FormAttachments";
import RequestStatusBadge from "./RequestStatusBadge";
import { RequestBaseFields, RequestStatus } from "../types/request.types";
import Can from "./Can";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { requestResourceUrl } from "../lib/mts-url";
import csrfToken from "../lib/csrf-token";
import { AttachmentModel } from "../types/attachment.types";

const renderStatusOption: SelectProps['renderOption'] = ({ option, checked }) => (
  option.label && option.label.length > 0 ?
    <RequestStatusBadge status={option.label as RequestStatus} /> :
    ''
);

const requestStatuses: RequestStatus[] = [
  'New',
  'Denied',
  'Approved',
  'Unapproved',
  'In progress',
  'Waiting for approval',
  'On Hold',
  'At printer',
  'Completed',
];

interface CommentFormFields {
  content?: string;
  request_status: string | null;
}

interface CommentFormProps {
  request: RequestBaseFields;
  onSubmit?: Function;
}

export default function CommentForm(props: CommentFormProps) {
  const { request, onSubmit } = props;
  const filesCountRef = useRef(0);
  const tempId = useTempId();
  const editorRef = useRef<Editor>(null);
  const queryClient = useQueryClient();
  
  const form = useForm<CommentFormFields>({
    initialValues: {
      content: '',
      request_status: null
    },
    validate: {
      content: (value, values) => {
        if (
          editorRef.current?.getText().length === 0 &&
          form.values.request_status === null &&
          filesCountRef.current === 0
        ) {
          return 'Please fill out this field.'
        }
      }
    }
  });

  const addComment = useMutation({
    mutationFn: (newComment: CommentFormFields) => {
      return axios.post(
        requestResourceUrl(request, 'comments/create'),
        {
          comment: { content: newComment.content },
          request_status: newComment.request_status,
          model_class: request.request_type,
          temp_attachable_id: tempId
        },
        {
          withCredentials: true,
          headers: { 'X-CSRF-Token': csrfToken() }
        }
      );
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [requestResourceUrl(request, 'comments')]
      });

      if (form.values.request_status !== null) {
        queryClient.invalidateQueries({ queryKey: ['requests', 'all'] });
        queryClient.invalidateQueries({
          queryKey: [request.request_type + 's', '' + request.id]
        });
      }

      onSubmit && onSubmit();
    },
    onError: (error, variables, context) => {
      notifications.show({
        message: `There was an error submitting the comment. Please try again.`,
        icon: <IconX />,
        color: 'red',
        position: 'top-center'
      });
    }
  });

  const onFormSubmit = useCallback((submitVals: CommentFormFields) => {
    const newComment = { ...submitVals };
    newComment.content = editorRef.current?.getHTML();
    addComment.mutate(newComment);
  }, []);
  
  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <ContentEditor<CommentFormFields>
        formKey="content"
        label="New Comment"
        editorRef={editorRef}
        form={form}
      />

      <FormAttachments
        onUpdate={(files: AttachmentModel[]) =>
          filesCountRef.current = files.length
        }
        tempId={tempId}
        mt="md"
      />

      <Can do="update_status" on="Request">
        <Select
          {...form.getInputProps('request_status')}
          key={form.key('request_status')}
          label="Update Status"
          placeholder="Select a new status"
          maw={rem(230)}
          value={form.values.request_status}
          onChange={value => form.setFieldValue('request_status', value)}
          rightSectionPointerEvents="all"
          renderOption={renderStatusOption}
          rightSection={
            form.values.request_status !== null && (
              <ActionIcon
                variant="transparent"
                onClick={() => form.setFieldValue('request_status', null)}
              >
                <IconX color="gray" />
              </ActionIcon>
            )
          }
          data={requestStatuses.filter(rs => rs !== request.status)}
        />
      </Can>

      <Group justify="flex-end" mt="md" p="sm">
        <Button type="submit">Submit Comment</Button>
      </Group>
    </form>
  );
}
